import { useCallback, useEffect, useMemo, useState } from 'react';
import { IServiceListItem } from '../types';
import {
	getSelectionHomogeneity,
	readBulkSelection,
	saveBulkSelection,
	SelectedServiceRef,
	selectionKey,
} from '../helpers/servicesBulkSelection';

function toSelectedRef(row: IServiceListItem): SelectedServiceRef {
	return {
		serviceId: row.id,
		saleOrderId: row.saleOrderId,
		serviceType: row.serviceType,
		reference: row.reference ?? '',
		agencyId: row.agencyId,
		tariffProviderFantasyName: row.tariffProviderFantasyName,
	};
}

export type UseServicesBulkSelectionResult = {
	selection: SelectedServiceRef[];
	selectedIds: Set<string>;
	homogeneity: ReturnType<typeof getSelectionHomogeneity>;
	isSelected: (serviceId: string) => boolean;
	toggleRow: (row: IServiceListItem) => void;
	togglePage: (rows: IServiceListItem[], selectAll: boolean) => void;
	clearSelection: () => void;
	count: number;
};

export const useServicesBulkSelection = (): UseServicesBulkSelectionResult => {
	const [selection, setSelection] = useState<SelectedServiceRef[]>(() => readBulkSelection());

	useEffect(() => {
		saveBulkSelection(selection);
	}, [selection]);

	const selectedIds = useMemo(() => new Set(selection.map(selectionKey)), [selection]);

	const homogeneity = useMemo(() => getSelectionHomogeneity(selection), [selection]);

	const isSelected = useCallback((serviceId: string) => selectedIds.has(serviceId), [selectedIds]);

	const toggleRow = useCallback((row: IServiceListItem) => {
		setSelection((prev) => {
			const key = row.id;
			const exists = prev.some((item) => item.serviceId === key);
			if (exists) {
				return prev.filter((item) => item.serviceId !== key);
			}
			return [...prev, toSelectedRef(row)];
		});
	}, []);

	const togglePage = useCallback((rows: IServiceListItem[], selectAll: boolean) => {
		setSelection((prev) => {
			const pageIds = new Set(rows.map((row) => row.id));
			if (!selectAll) {
				return prev.filter((item) => !pageIds.has(item.serviceId));
			}
			const map = new Map(prev.map((item) => [item.serviceId, item]));
			rows.forEach((row) => {
				map.set(row.id, toSelectedRef(row));
			});
			return Array.from(map.values());
		});
	}, []);

	const clearSelection = useCallback(() => setSelection([]), []);

	return {
		selection,
		selectedIds,
		homogeneity,
		isSelected,
		toggleRow,
		togglePage,
		clearSelection,
		count: selection.length,
	};
};
